﻿import { defineComponent } from 'vue';
import { useBookingStore } from '../../../store/bookingstore';

import passenger_form from "./passenger.vue";

import { booking_search_group } from "../../../objects/v1_2/Booking_Details/booking_search_group";
import { passenger } from "../../../objects/v1_2/Booking/Passenger";
import { country_nationality } from "../../../objects/v1_3/web_engine/country_nationality";

import { Web_Engine } from "../../../services/web_engine";
import { Booking_Passengers, Draft_From_Passenger, Is_Required, Rules_Context, Split_Phone, Validate_Passenger } from "./passenger_rules";

const Web = new Web_Engine();

export default defineComponent({
    name: "passengers",
    components: {
        passenger_form
    },
    setup() {
        const bookingStore = useBookingStore();

        return {
            bookingStore
        }
    },
    data() {
        return {
            countries: [] as country_nationality[]
        }
    },
    computed: {
        Passengers(): passenger[] {
            return Booking_Passengers(this.$store.getters.get_Booking as booking_search_group);
        },
        Incomplete_Passengers(): { index: number, passenger: passenger, count: number }[] {
            const ctx = Rules_Context(this.$store.getters.get_Booking);
            return this.Passengers
                .map((p, index) => ({
                    index,
                    passenger: p,
                    count: Object.keys(Validate_Passenger(p, this.bookingStore.passenger_drafts[p.passenger_identifier ?? ""] ?? Draft_From_Passenger(p), ctx)).length
                }))
                .filter(item => item.count > 0);
        }
    },
    watch: {
        Passengers: {
            handler() {
                this.Ensure_Drafts();
            },
            immediate: true
        }
    },
    methods: {
        isAgent_Login(): boolean {
            return this.$store.getters.get_Agent_Login?.valid_login == true;
        },
        GetBrand(): string {
            return String(import.meta.env.VITE_DEFAULT_CLASS);
        },
        // Creates the form draft for passengers seen for the first time in this session.
        Ensure_Drafts() {
            const ctx = Rules_Context(this.$store.getters.get_Booking);
            const clear_placeholders = !this.isAgent_Login();

            this.Passengers.forEach(p => {
                const id = p.passenger_identifier ?? "";
                let draft = this.bookingStore.passenger_drafts[id];

                if (!draft) {
                    draft = Draft_From_Passenger(p);
                    // New consumer bookings carry placeholder names; remember and clear them so the customer types real ones.
                    if (clear_placeholders) {
                        if (Is_Required(p, 'first_name', ctx)) {
                            draft.placeholder_first_name = p.first_name ?? "";
                        }
                        if (Is_Required(p, 'family_name', ctx)) {
                            draft.placeholder_family_name = p.family_name ?? "";
                        }
                    }
                    this.bookingStore.passenger_drafts[id] = draft;
                }

                // Also covers a page refresh, where the stored booking still has the placeholders. Real names are kept.
                if (clear_placeholders && draft.placeholder_first_name !== "" && p.first_name === draft.placeholder_first_name) {
                    p.first_name = "";
                }
                if (clear_placeholders && draft.placeholder_family_name !== "" && p.family_name === draft.placeholder_family_name) {
                    p.family_name = "";
                }
            });
            this.Split_Seeded_Phones();
        },
        // A number loaded from the booking is stored as code + number; split it once countries are known.
        Split_Seeded_Phones() {
            if (this.countries.length === 0) {
                return;
            }
            this.Passengers.forEach(p => {
                const draft = this.bookingStore.passenger_drafts[p.passenger_identifier ?? ""];
                if (!draft) {
                    return;
                }
                if (draft.phone_code === "" && draft.phone_number !== "" && draft.phone_number === (p.telephone_number ?? "")) {
                    const split = Split_Phone(draft.phone_number, this.countries);
                    if (split) {
                        draft.phone_code = split.code;
                        draft.phone_number = split.number;
                    }
                }
                if (draft.emergency_code === "" && draft.emergency_number !== "" && draft.emergency_number === (p.emergency_number ?? "")) {
                    const split = Split_Phone(draft.emergency_number, this.countries);
                    if (split) {
                        draft.emergency_code = split.code;
                        draft.emergency_number = split.number;
                    }
                }
            });
        },
        async Load_Countries() {
            try {
                const countries = (await Web.Get_Countries()).countries ?? [];
                this.countries = countries.filter(c => c.country && c.country.toLowerCase() !== "null");
                this.Split_Seeded_Phones();
            } catch (error) {
                console.error("Could not load countries", error);
            }
        },
        Passenger_Label(item: { index: number, passenger: passenger }): string {
            const name = ((item.passenger.first_name ?? "") + " " + (item.passenger.family_name ?? "")).trim();
            return "Passenger " + (item.index + 1) + (name ? " (" + name + ")" : "");
        },
        Go_To_Passenger(p: passenger) {
            const card = document.getElementById("passenger_" + p.passenger_identifier);
            card?.scrollIntoView({ behavior: "smooth", block: "start" });
            card?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus({ preventScroll: true });
        }
    },
    mounted() {
        // Errors for every field only appear once the customer tries to continue on this visit.
        this.bookingStore.show_passenger_errors = false;
        this.Load_Countries();
    }
})
